import React, { useEffect, useState } from "react";
import ScrollFade from "../ScrollFade";
import axios from "axios";
import { BarChart3, Crown, Film, Volume2, CheckCircle2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF37";
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const Stat = ({ testid, icon: Icon, label, value, sub }) => (
  <div data-testid={testid} className="rounded-md border border-white/10 bg-black/40 p-3">
    <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.2em] text-white/45">{label}<Icon size={12} style={{ color: GOLD }} /></div>
    <div className="font-cover text-3xl leading-none text-white mt-1">{value}</div>
    {sub && <div className="text-[10px] text-white/40 mt-1">{sub}</div>}
  </div>
);

// Coach Marketing Kit: per-UTM-variant funnel (visits -> hero sound-on -> checkouts -> paid sales) + hero film stats.
export default function AdAttribution() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = () => {
    const token = localStorage.getItem("coachToken");
    setLoading(true);
    axios.get(`${API}/coach/attribution`, { params: { days }, headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [days]); // eslint-disable-line react-hooks/exhaustive-deps
  const hf = data?.hero_film, top = data?.top_source, rows = data?.rows || [], t = data?.totals;
  return (
    <div data-testid="ad-attribution" className="rounded-lg border border-[#D4AF37]/50 bg-[#0d0d0d] p-4 mb-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 font-headings text-lg uppercase tracking-wide"><BarChart3 size={16} className="text-[#D4AF37]" /> Ad Attribution · What Actually Converts</div>
          <p className="text-xs text-white/55 mt-1">Every tracked link is followed from landing visit to "Tap for sound" play to checkout to paid sale. Sorted by sales, then revenue.</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {[7, 30, 90].map((d) => <button key={d} data-testid={`attribution-days-${d}`} onClick={() => setDays(d)} aria-pressed={days === d} className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-sm border transition-colors ${days === d ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/60 hover:text-white"}`}>{d}d</button>)}
          <button data-testid="attribution-refresh" onClick={load} aria-label="Refresh" className="w-7 h-7 rounded-sm border border-white/15 flex items-center justify-center text-white/50 hover:text-white"><RefreshCw size={12} className={loading ? "animate-spin" : ""} /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
        <Stat testid="hero-stat-views" icon={Film} label="Hero film views" value={hf?.views ?? 0} sub={`last ${days} days`} />
        <Stat testid="hero-stat-sound" icon={Volume2} label="Tap-for-sound plays" value={hf?.sound_on ?? 0} sub={`${hf?.sound_on_rate ?? 0}% of views`} />
        <Stat testid="hero-stat-completion" icon={CheckCircle2} label="Completion rate" value={`${hf?.completion_rate ?? 0}%`} sub={`${hf?.completes ?? 0} watched to the end`} />
        <div data-testid="top-source-card" className="rounded-md border p-3" style={{ borderColor: `${GOLD}80`, background: "linear-gradient(135deg, rgba(212,175,55,0.14), rgba(0,0,0,0.4))" }}>
          <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: GOLD }}>Top converting source · 7d<Crown size={12} style={{ color: GOLD }} /></div>
          <div className="font-cover text-3xl leading-none text-white mt-1 truncate" data-testid="top-source-name">{top ? top.source : "—"}</div>
          <div className="text-[10px] text-white/55 mt-1" data-testid="top-source-sub">{top ? `${top.sales} paid sale${top.sales === 1 ? "" : "s"} · ${fmtMoney(top.revenue)}` : "No paid sales in the last 7 days yet"}</div>
        </div>
      </div>

      <ScrollFade testId="attribution-scroll" className="rounded-md border border-white/10">
        <table data-testid="attribution-table" className="w-full text-xs min-w-[760px]">
          <thead>
            <tr className="bg-black/50 font-mono text-[9px] uppercase tracking-[0.18em] text-white/45">
              {["Source", "Campaign", "Content", "Visits", "Sound-on", "Checkouts", "Sales", "Revenue", "Conv %"].map((h, i) => <th key={h} className={`px-3 py-2 ${i < 3 ? "text-left" : "text-right"}`}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} data-testid="attribution-empty" className="px-3 py-6 text-center text-white/40">No tracked traffic yet. Share a tracked link from the Ad Copy Pack above and the rows will appear here.</td></tr>}
            {rows.map((r, i) => (
              <tr key={`${r.source}-${r.campaign}-${r.content}`} data-testid={`attribution-row-${i}`} className="border-t border-white/5 hover:bg-white/[0.03]">
                <td className="px-3 py-2 font-bold text-white/90 flex items-center gap-1.5">{i === 0 && r.sales > 0 && <Crown size={11} style={{ color: GOLD }} />}{r.source}</td>
                <td className="px-3 py-2 text-white/70">{r.campaign}</td>
                <td className="px-3 py-2 text-white/70">{r.content}</td>
                <td className="px-3 py-2 text-right text-white/80">{r.visits}</td>
                <td className="px-3 py-2 text-right text-white/80">{r.sound_on}</td>
                <td className="px-3 py-2 text-right text-white/80">{r.checkouts}</td>
                <td className="px-3 py-2 text-right font-bold" style={{ color: r.sales ? GOLD : "rgba(255,255,255,0.5)" }}>{r.sales}</td>
                <td className="px-3 py-2 text-right text-white/80">{fmtMoney(r.revenue)}</td>
                <td className="px-3 py-2 text-right text-white/80">{r.conversion_rate}%</td>
              </tr>
            ))}
          </tbody>
          {t && rows.length > 0 && (
            <tfoot>
              <tr data-testid="attribution-totals" className="border-t border-[#D4AF37]/40 bg-black/40 font-bold text-white/85">
                <td className="px-3 py-2" colSpan={3}>Total</td>
                <td className="px-3 py-2 text-right">{t.visits}</td><td className="px-3 py-2 text-right">{t.sound_on}</td><td className="px-3 py-2 text-right">{t.checkouts}</td>
                <td className="px-3 py-2 text-right" style={{ color: GOLD }}>{t.sales}</td><td className="px-3 py-2 text-right">{fmtMoney(t.revenue)}</td>
                <td className="px-3 py-2 text-right">{t.visits ? Math.round((t.sales / t.visits) * 1000) / 10 : 0}%</td>
              </tr>
            </tfoot>
          )}
        </table>
      </ScrollFade>
    </div>
  );
}
